import DateHelpers from '../../../../helpers/dateHelpers';
import { useCallback, useEffect, useState } from 'react';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { inspectionDB } from '../../../../data/database.structure';
import { useNavigate, useParams } from 'react-router';
import {
  TableOfContentsRenderer,
  TableOfContentsSection,
} from '../../../../components/templates/TableOfContentsRenderer';
import { EquipmentPartModel } from '../../../../data/types';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import DatabaseAssetsMethods from '../../../../data/database.assets.methods';
import { taskStatusfromProgress } from '../../../../data/enums';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import { Button } from '../../../../components/atoms/Button';
import { ArrowLeft } from 'lucide-react';
import RollerAssemblyService from '../../../../services/rollerAssembly';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';

interface InspectionData {
  id: string;
  assetName: string;
  assetType: string;
  assetMode: string;
  siteName: string;
  customerName: string;
  timestamp: string;
  sections: TableOfContentsSection[];
}

interface UndercarriageRollerAssembliesTableOfContentsScreenProps {
  rollerName: string;
  partCategoryName: string;
  headerTitle?: string;
  subHeaderTitle?: string;
}

export const UndercarriageRollerAssembliesTableOfContentsScreen = ({
  rollerName,
  partCategoryName,
  headerTitle,
  subHeaderTitle,
}: UndercarriageRollerAssembliesTableOfContentsScreenProps) => {
  const { externalId, sideName } = useParams();
  const { inspection } = useInspectionCached(externalId);
  const navigate = useNavigate();
  const [inspectionData, setInspectionData] = useState<InspectionData | null>(
    null,
  );
  const [equipmentParts, setEquipmentParts] = useState<
    EquipmentPartModel[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  useEffect(() => {
    setError(null); // reset error
    async function getInspection() {
      try {
        if (inspection) {
          const equipments = await inspectionDB.equipments
            .where('assetId')
            .equals(inspection.asset?.id ?? '')
            .toArray();

          const sideNameDefined = sideName
            ?.split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          const equipment = equipments.find((equipment) => {
            return equipment.name === sideNameDefined;
          });

          if (!equipment) {
            setError('Equipment not found');
            return;
          }

          const assetMethods = new DatabaseAssetsMethods(inspectionDB);

          const equipmentParts = await assetMethods.getAssetEquipmentParts(
            equipment.externalId,
            partCategoryName,
            rollerName,
          );

          // setEquipment(equipment);
          setEquipmentParts(equipmentParts);
        }
      } catch (error) {
        console.error('Error fetching inspection', error);
        setError('Error fetching inspection');
      }
    }
    if (inspection) {
      getInspection();
    }
  }, [inspection]);

  useEffect(() => {
    if (inspection) {
      const sections: TableOfContentsSection = {
        id: 'roller-assemblies',
        // TableOfContentsRenderer hides the heading when title is empty (#10549);
        // 'Positions' matches the Bucket GET position list's section heading.
        title: 'Positions',
        order: 1,
        items:
          [...(equipmentParts ?? [])]
            .sort((a, b) => a.position - b.position)
            .map((equipmentPart) => {
              const completionPercentage =
                RollerAssemblyService.getRollerAssemblyCompletePercentage(
                  rollerName,
                  inspection.inspection.inspectionCategoryTypes,
                  equipmentPart,
                );
              return {
                id: `roller-assemblies-${equipmentPart.position}`,
                order: equipmentPart.position,
                label: 'Position ' + equipmentPart.position,
                status: taskStatusfromProgress(completionPercentage),
                enabled: true,
                url: `${equipmentPart.id}/${equipmentPart.position}`,
                inspectionId: inspection.inspection.externalId,
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              };
            }) || [],
      };

      const inspectionData: InspectionData = {
        id: inspection.inspection.externalId,
        assetName: inspection.asset?.name || '',
        assetType: inspection.asset?.model.assetType || '',
        assetMode: inspection.asset?.model.name || '',
        siteName: inspection.asset?.siteName || '',
        customerName: inspection.asset?.customerName || '',
        timestamp: DateHelpers.displayShortTimestamp({
          timestamp: inspection.inspection.inspectionDate,
          convertToCommonTime: inspection.inspection.isSynced,
        }),
        sections: [sections],
      };

      setInspectionData(inspectionData);
    }
  }, [inspection, equipmentParts]);

  const markAsComplete = useCallback(async () => {
    navigate(-1);
  }, []);

  if (!inspection || !inspectionData || !sideName || !undercarriageCategory) {
    return <ScreenSkeleton fields={8} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <div className="flex flex-col">
      <ScreenHeading
        sticky
        tooltipKey={`undercarriage-roller-assemblies-heading-screen`}
        title={
          subHeaderTitle ??
          `${headerTitle ?? 'Undercarriage'} ${sideName.replace('-', ' ')}`
        }
        tag={sideName.split('-')[0]}
      />
      <div className="mt-10">
        {/* ToC Content */}
        <TableOfContentsRenderer
          sections={inspectionData.sections}
          header={null}
          footer={null}
        />
      </div>
      <div className="mt-10 flex justify-center">
        <Button
          children="Mark as complete"
          className="shadow-sm"
          prefixIcon={<ArrowLeft color="white" />}
          disabled={false}
          onClick={() => markAsComplete?.()}
        />
      </div>
    </div>
  );
};
